import { useState, useRef, useCallback } from "react";
import { getLanguageName as getSharedLanguageName, normalizeLanguage } from "../utils/languages";

export const getLanguageName = (code) => {
  if (!code) return "Unknown";
  const normalized = normalizeLanguage(code);
  return getSharedLanguageName(normalized) || code;
};

const useEmbeddedSubtitles = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const ffmpegRef = useRef(null);

  const loadFFmpeg = useCallback(async () => {
    if (ffmpegRef.current) {
      return ffmpegRef.current;
    }

    setIsLoading(true);
    setLoadingMessage("در حال بارگذاری ابزار (فقط بار اول طول می‌کشد)...");

    try {
      const { FFmpeg } = await import("@ffmpeg/ffmpeg");
      const ffmpeg = new FFmpeg();

      let coreURL = "/ffmpeg/ffmpeg-core.esm.js";
      let wasmURL = "/ffmpeg/ffmpeg-core.wasm";

      try {
        const cache = await caches.open("ffmpeg-cache-v7");
        const [coreResp, wasmResp] = await Promise.all([
          cache.match(coreURL),
          cache.match(wasmURL),
        ]);
        if (coreResp && wasmResp) {
          coreURL = URL.createObjectURL(await coreResp.blob());
          wasmURL = URL.createObjectURL(await wasmResp.blob());
        }
      } catch {}

      await ffmpeg.load({
        coreURL,
        wasmURL,
      });

      ffmpegRef.current = ffmpeg;
      return ffmpeg;
    } catch (error) {
      console.error("Error loading FFmpeg:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getEmbeddedSubtitles = useCallback(
    async (file) => {
      if (!file) return [];

      setIsLoading(true);

      try {
        const ffmpeg = await loadFFmpeg();
        const { fetchFile } = await import("@ffmpeg/util");

        // فقط 2MB اول - برای هدر و متادیتا کافیه
        setLoadingMessage("در حال خواندن اطلاعات فیلم...");
        const headerBlob = file.slice(0, 2 * 1024 * 1024);

        await ffmpeg.writeFile("header.mkv", await fetchFile(headerBlob));

        // جمع‌آوری خروجی
        let outputData = "";

        const logHandler = ({ message }) => {
          outputData += message + "\n";
        };

        ffmpeg.on("log", logHandler);

        // استخراج اطلاعات استریم‌ها
        await ffmpeg.exec(["-i", "header.mkv", "-f", "null", "-"]);

        // پاکسازی
        ffmpeg.off("log", logHandler);
        await ffmpeg.deleteFile("header.mkv");

        // پارس کردن خروجی
        const lines = outputData.split("\n");
        const subtitles = [];

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];

          // یافتن خطوط Stream که حاوی Subtitle هستن
          if (line.includes("Stream #") && line.includes("Subtitle")) {
            // استخراج شماره استریم
            const streamMatch = line.match(/Stream #(\d+:\d+)/);
            const streamId = streamMatch ? streamMatch[1] : "";

            // استخراج کد زبان از پرانتز
            const langMatch = line.match(/\((\w+)\)/);
            let language = "und";
            if (langMatch) {
              language = langMatch[1].toLowerCase();
            }

            // تشخیص فرمت
            let format = "unknown";
            if (
              line.toLowerCase().includes("subrip") ||
              line.toLowerCase().includes("srt")
            )
              format = "srt";
            else if (
              line.toLowerCase().includes("ass") ||
              line.toLowerCase().includes("ssa")
            )
              format = "ass";
            else if (
              line.toLowerCase().includes("dvd_subtitle") ||
              line.toLowerCase().includes("vobsub")
            )
              format = "vobsub";
            else if (
              line.toLowerCase().includes("webvtt") ||
              line.toLowerCase().includes("vtt")
            )
              format = "vtt";
            else if (
              line.toLowerCase().includes("pgs") ||
              line.toLowerCase().includes("hdmv")
            )
              format = "pgs";
            else if (
              line.toLowerCase().includes("mov_text") ||
              line.toLowerCase().includes("tx3g")
            )
              format = "tx3g";

            // استخراج عنوان از خطوط Metadata بعدی
            let title = "";
            for (let j = i + 1; j < Math.min(i + 15, lines.length); j++) {
              const metaLine = lines[j];
              if (metaLine.includes("Stream #")) break;

              const titleMatch = metaLine.match(/title\s*:\s*(.+)/i);
              if (titleMatch) {
                title = titleMatch[1].trim();
                break;
              }
            }

            if (!title) {
              title = `${getLanguageName(language)} ${format.toUpperCase()}`;
            }

            subtitles.push({
              index: streamId || `0:${subtitles.length}`,
              language: language,
              title: title,
              format: format,
              trackId: streamId,
            });
          }
        }

        return subtitles;
      } catch (error) {
        console.error("Error in getEmbeddedSubtitles:", error);
        throw error;
      } finally {
        setIsLoading(false);
        setLoadingMessage("");
      }
    },
    [loadFFmpeg],
  );

  // ========== تابع جدید: استخراج محتوای واقعی زیرنویس ==========
  const extractSubtitleContent = useCallback(
    async (file, trackIndex) => {
      if (!file) {
        throw new Error("فایلی انتخاب نشده است");
      }

      setIsLoading(true);
      setLoadingMessage("در حال استخراج متن زیرنویس...");

      try {
        const ffmpeg = await loadFFmpeg();
        const { fetchFile } = await import("@ffmpeg/util");

        // نوشتن فایل کامل
        await ffmpeg.writeFile("input.mkv", await fetchFile(file));

        // استخراج زیرنویس به فرمت SRT
        const outputFileName = `subtitle_${trackIndex}.srt`;

        await ffmpeg.exec([
          "-i",
          "input.mkv",
          "-map",
          `0:s:${trackIndex}`,
          "-c:s",
          "srt",
          outputFileName,
        ]);

        // خواندن فایل استخراج شده
        const data = await ffmpeg.readFile(outputFileName);
        const subtitleText = new TextDecoder().decode(data);

        // پاکسازی فایل‌های موقت
        await ffmpeg.deleteFile("input.mkv");
        await ffmpeg.deleteFile(outputFileName);

        return subtitleText;
      } catch (error) {
        console.error("Error extracting subtitle:", error);
        throw new Error("خطا در استخراج زیرنویس: " + error.message);
      } finally {
        setIsLoading(false);
        setLoadingMessage("");
      }
    },
    [loadFFmpeg],
  );

  return {
    isLoading,
    loadingMessage,
    getEmbeddedSubtitles,
    extractSubtitleContent, // خروجی جدید
  };
};

export default useEmbeddedSubtitles;
